import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import SideNav from '../sidenav';
import { ReactComponent as Logo } from '$/assets/BixLogo.svg';
import { ReactComponent as InvertedLogo } from '$/assets/logo-inverted.svg';
import { ReactComponent as LogoutIcon } from '$/assets/icons/Logout.svg';
import { ReactComponent as SettingsIcon } from '$/assets/icons/settings.svg';
import * as Bixby from '$/core';
import { UserStatus } from '$/core/enums';
import SearchBox from '../searchBox';
import UserStatusDropdown, { StatusIcon } from '../user-status-dropdown';
import { History } from '$/services';
import './index.scss';

export interface ITopNavProps {
  welcomeMessage?: string;
  feedbackUrl?: string;
  showAdminLink: boolean;
  onAdminPortal: boolean;
  onSearchChange?: (searchText: string) => void | Promise<void>;
  searchResults?: Bixby.Models.SearchResult[];
  onSearchResultsClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void | Promise<void>;
  contactUsLink?: string;
  featureFlags?: string[];
  firstName?: string;
  lastName?: string;
  email?: string;
  isAdmin?: boolean;
  userStatus?: UserStatus | null;
  onLoadUserStatus: () => void;
  onUpdateUserStatus?: (status: UserStatus) => Promise<void> | void;
}

const TopNav: React.FC<ITopNavProps> = props => {
  const {
    welcomeMessage,
    feedbackUrl,
    showAdminLink,
    onAdminPortal,
    onSearchChange,
    searchResults,
    onSearchResultsClick,
    contactUsLink,
    firstName,
    lastName,
    email,
    isAdmin,
    userStatus,
    onLoadUserStatus,
    onUpdateUserStatus,
  } = props;
  const featureFlags: string[] = props.featureFlags || [];
  const isStandAlone = Bixby.Configuration.isStandAloneMode;
  const pathForRedirectOnAuth = isStandAlone ? '/portfolio' : '/admin';
  const [menuOpen, setMenuOpen] = useState(false);
  const [profilePopupOpen, setProfilePopupOpen] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);

  useEffect(() => {
    if (isAdmin) onLoadUserStatus();
  }, [isAdmin, onLoadUserStatus]);

  const handleStatusChange = async (status: UserStatus) => {
    setStatusUpdating(true);
    try {
      await onUpdateUserStatus?.(status);
    } finally {
      setStatusUpdating(false);
    }
  };
  const profilePopupRef = useRef<HTMLDivElement>(null);

  History.listen(() => setMenuOpen(false));

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profilePopupRef.current && !profilePopupRef.current.contains(event.target as Node)) {
        setProfilePopupOpen(false);
      }
    };
    if (profilePopupOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profilePopupOpen]);

  const menuToggle = () => {
    setMenuOpen(!menuOpen);
  };

  const handleFeedbackAction = () => {
    window.open(feedbackUrl);
  };

  const displayName = firstName && lastName ? `${firstName} ${lastName}` : email || 'User';

  return (
    <div className={classNames('topNav', { active: menuOpen, invert: onAdminPortal })}>
      <Link to={pathForRedirectOnAuth} className="logo_wrap">
        {onAdminPortal && <InvertedLogo />}
        {!onAdminPortal && <Logo />}
      </Link>
      <div className="search_export_combo">
        <SearchBox
          placeholder="Search for Companies"
          onChange={onSearchChange}
          searchResults={searchResults}
          onResultsClick={onSearchResultsClick}
        />
        {showAdminLink && (
          <Link className="nav_portal" to="/admin">
            Admin Portal
          </Link>
        )}
      </div>
      {welcomeMessage && <div className="welcome_message">{welcomeMessage}</div>}
      {isStandAlone && (
        <Link to="/settings" className="top_settings">
          <div className="settings_icon">
            <SettingsIcon />
          </div>
          Settings
        </Link>
      )}
      <Link to="/logout" className="top_logout">
        <div className="logout_icon">
          <LogoutIcon />
        </div>
        Logout
      </Link>
      {isStandAlone && (
        <button role="link" onClick={handleFeedbackAction} className="feedback_item">
          Send Feedback
        </button>
      )}
      {isAdmin && (firstName || lastName || email) && (
        <div className="user-initials-avatar-wrap" ref={profilePopupRef}>
          <button
            className={classNames(
              'user-initials-avatar',
              isAdmin && userStatus ? `user-initials-avatar--${userStatus.toLowerCase().replaceAll(/\s+/g, '')}` : '',
              { active: profilePopupOpen }
            )}
            onClick={() => setProfilePopupOpen(prev => !prev)}
            aria-label={`User profile${isAdmin && userStatus ? `, status: ${userStatus}` : ''}`}
            aria-expanded={profilePopupOpen}>
            {firstName?.[0]?.toUpperCase() ?? ''}
            {lastName?.[0]?.toUpperCase() ?? ''}
            {isAdmin && (
              <span className="avatar-status-badge" aria-hidden="true">
                {userStatus ? <StatusIcon status={userStatus} /> : null}
              </span>
            )}
          </button>
          {profilePopupOpen && (
            <div className="user-profile-popup">
              <div className="user-profile-popup-info">
                <div className="user-profile-popup-avatar">
                  {firstName?.[0]?.toUpperCase() ?? ''}
                  {lastName?.[0]?.toUpperCase() ?? ''}
                </div>
                <div className="user-profile-popup-name">{displayName}</div>
              </div>
              {isAdmin && userStatus && (
                <UserStatusDropdown
                  currentStatus={userStatus}
                  onStatusChange={handleStatusChange}
                  isUpdating={statusUpdating}
                />
              )}
            </div>
          )}
        </div>
      )}
      <div className="burger_wrap" onClick={menuToggle} />
      <SideNav featureFlags={featureFlags} onAdminPortal={onAdminPortal} contactUsLink={contactUsLink} />
    </div>
  );
};

export default TopNav;
